import React, { useEffect, useRef, useMemo } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import { useTranslation } from 'react-i18next';
import * as S from './Carousel.styled';
import { AdKind } from '@/api/types/common.types';
import { PhotoOverlay } from '@/components/PhotoOverlay';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { KindIcons } from '@/components/KindIcons/KindIcons';
import { useGalleryStore } from '@/store/useGalleryStore';
import { useResponsive } from '@/hooks/useResponsive';

export const Carousel = ({
  images,
  loopSingleImage = false,
  title,
  dataStat,
  kinds,
  loading,
  sliderId,
  openGallery,
  overlayText,
}: {
  loading: boolean;
  images: string[];
  title: string;
  loopSingleImage: boolean;
  sliderId: string;
  dataStat: string;
  kinds?: AdKind[];
  openGallery?: () => void;
  overlayText?: string;
}) => {
  const { isDesktop } = useResponsive();
  const { t } = useTranslation();
  const { currentIndex, setCurrentIndex, setSliderId } = useGalleryStore();
  const isInternalChangeRef = useRef(false);
  const totalSlides = images.length;
  const normalizedIndex =
    totalSlides > 0
      ? ((currentIndex % totalSlides) + totalSlides) % totalSlides
      : 0;
  const slidesCounter = `${normalizedIndex + 1} / ${totalSlides}`;
  const slicedSlides = images.slice(0, 9);

  useEffect(() => {
    setSliderId(sliderId);
    return () => {
      setSliderId(null);
    };
  }, [sliderId, setSliderId]);

  const [sliderRef, instanceRef] = useKeenSlider({
    loop: true,
    slides: {
      perView: 1,
    },
    mode: 'snap',
    dragSpeed: 0.4,
    rubberband: false,
    renderMode: 'performance',
    slideChanged: () => {
      if (instanceRef.current && totalSlides > 0) {
        const newIndex = instanceRef.current.track.details.rel ?? 0;
        const absoluteIndex = instanceRef.current.track.details.abs ?? newIndex;
        const normalizedIdx =
          ((absoluteIndex % totalSlides) + totalSlides) % totalSlides;
        isInternalChangeRef.current = true;

        setCurrentIndex(normalizedIdx);
        setTimeout(() => {
          isInternalChangeRef.current = false;
        }, 0);
      }
    },
  });

  useEffect(() => {
    if (
      instanceRef.current &&
      !isInternalChangeRef.current &&
      totalSlides > 0
    ) {
      const currentAbs = instanceRef.current.track.details.abs ?? 0;
      const currentNormalized =
        ((currentAbs % totalSlides) + totalSlides) % totalSlides;

      if (currentNormalized !== normalizedIndex) {
        instanceRef.current.moveToIdx(normalizedIndex);
      }
    }
  }, [normalizedIndex, totalSlides]);

  const handleOpenGallery = (idx: number) => {
    isInternalChangeRef.current = true;
    setCurrentIndex(idx);
    setTimeout(() => {
      isInternalChangeRef.current = false;
    }, 0);
    openGallery?.();
  };

  const slides = useMemo(() => {
    if (loopSingleImage) {
      return [...images, images[0]];
    }

    return images;
  }, [images, loopSingleImage]);

  function goPrev() {
    instanceRef.current?.prev();
  }

  function goNext() {
    instanceRef.current?.next();
  }

  const renderThumbs = () =>
    slicedSlides.map((imgUrl, index) => {
      const restItems = totalSlides - slicedSlides.length;
      const isLastItem = index === 8 && restItems > 0;

      return (
        <S.Thumb
          key={imgUrl}
          onClick={() => handleOpenGallery(index)}
          onMouseOver={() => instanceRef.current?.moveToIdx(index)}
        >
          <S.ThumbImage $isActive={currentIndex === index} src={imgUrl} />
          {isLastItem ? (
            <S.ThumbLabel>
              +{restItems} {t('common.photos')}
            </S.ThumbLabel>
          ) : null}
        </S.Thumb>
      );
    });

  return (
    <>
      <S.SliderWrapper $loading={loading}>
        {!!overlayText && <PhotoOverlay overlayText={overlayText} zIndex={2} />}
        <S.Slider
          ref={sliderRef}
          role="presentation"
          $photosCount={images.length}
          className="keen-slider"
        >
          {slides.map((src, i) => (
            <S.Slide key={`${i}-${src}`} className="keen-slider__slide">
              {isDesktop && <S.SlideWrapper $photo={src} />}
              <S.SlideImage
                src={src}
                width={660}
                height={496}
                {...(dataStat ? { dataStat } : {})}
                {...(i ? { loading: 'lazy' } : { fetchpriority: 'high' })}
                onClick={() => handleOpenGallery(i)}
                alt={title + (i ? ` (${i + 1})` : '')}
                data-testid={AdPageTestIds.slider.image}
              />
            </S.Slide>
          ))}
        </S.Slider>
        <KindIcons kinds={kinds} />

        <S.Count data-testid={AdPageTestIds.slider.counter}>
          {slidesCounter}
        </S.Count>
        {isDesktop && (
          <>
            <S.ZoomButton onClick={() => handleOpenGallery(normalizedIndex)} />
            <S.ArrowButton $direction="left" onClick={goPrev} />
            <S.ArrowButton $direction="right" onClick={goNext} />
          </>
        )}
      </S.SliderWrapper>
      <div id={sliderId} />
      {isDesktop && <S.Thumbs>{renderThumbs()}</S.Thumbs>}
    </>
  );
};
